import * as React from 'react';
import { HelpCircle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

/**
 * Aide à la demande: une petite icône « ? » à côté d'un titre ou d'un chiffre.
 *
 * Les explications (« comment c'est calculé », listes de notes) ne prennent
 * plus de place sur la page: elles s'ouvrent dans une bulle au clic, au
 * toucher sur téléphone, ou au clavier (Tab puis Entrée ou Espace; Échap
 * ferme). Une infobulle au survol ne marchait ni au doigt ni au clavier.
 *
 *  - `titre`    ce que l'aide explique: nom du bouton (« Aide : … ») et
 *               titre en gras dans la bulle;
 *  - `children` le texte (ou des liens);
 *  - `points`   une liste de phrases, affichées en liste dans la bulle;
 *  - `libelle`  un court texte visible à côté de l'icône: l'aide devient
 *               une petite pastille (« Pièces pas encore reprises »);
 *  - `ton`      'neutre' (défaut) ou 'attention', pour la pastille.
 */
const TONS = {
  neutre: 'border-border bg-muted/40 text-muted-foreground hover:text-foreground',
  attention: 'border-flame/30 bg-flame/10 text-flame-ink hover:bg-flame/15',
};

export const Aide = ({ titre, children, points, libelle, ton = 'neutre', align = 'start', className }) => {
  const liste = (points || []).filter(Boolean);
  if (!children && liste.length === 0) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={libelle ? `${libelle} : en savoir plus` : `Aide : ${titre}`}
          data-testid="aide"
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            libelle
              ? cn('h-7 max-w-full gap-1.5 border px-2.5 text-xs font-medium', TONS[ton] || TONS.neutre)
              : 'h-6 w-6 text-muted-foreground hover:text-foreground',
            className,
          )}
        >
          <HelpCircle className={libelle ? 'h-3.5 w-3.5 shrink-0' : 'h-4 w-4'} aria-hidden="true" />
          {libelle && <span className="truncate">{libelle}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="w-80 max-w-[calc(100vw-2rem)] space-y-2 p-3 text-sm leading-relaxed"
        data-testid="aide-contenu"
      >
        {titre && <p className="font-semibold text-foreground">{titre}</p>}
        {children && <div className="text-muted-foreground">{children}</div>}
        {liste.length > 0 && (
          <ul className="list-disc space-y-1 pl-4 text-muted-foreground">
            {liste.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default Aide;
